import classNames from "classnames";
import React, { useEffect, useRef } from "react";
import { useTransitionState } from "react-transition-state";

export const ANIMATION_DURATION = 250;

/**
 * Notes:
 *  - use this component to wrap collapsible content, in particular where the content is of unknown of varying height.
 *  - be wary of Margins on the inner content as this can affect the height of the collapsed container.
 *  - other than the `open` boolean, this component accepts all the props of a `div`.
 */

export default function CollapsibleContainer({
  children,
  open,
  className,
  style,
  onTransitionEnd,
  ...divProps
}: Omit<React.HTMLAttributes<HTMLDivElement>, "onTransitionEnd"> & {
  readonly open: boolean;
  readonly onTransitionEnd?: (newOpenState: boolean) => void;
}) {
  const innerRef = useRef<HTMLDivElement>(null);

  const [{ status }, toggle] = useTransitionState({
    // If the duration is changed in future, make sure this value matches the CSS duration.
    timeout: ANIMATION_DURATION,
    // preEnter/preExit render an extra frame at the explicit pixel height before
    // the transition target is applied. Without this, closing races the browser's
    // style batching (it collapses `height: auto -> 0` in one commit, which CSS
    // can't animate) — this is why the exit animation was inconsistent across
    // browsers (worked on Windows Chrome, not on Mac Chrome).
    preEnter: true,
    preExit: true,
    initialEntered: open,
  });

  useEffect(() => {
    toggle(!!open);
  }, [open, toggle]);

  const isExpanded = status === "entered"; // fully open, height: auto
  // Frames that pin an explicit pixel height so the CSS height transition can run.
  const hasExplicitHeight = status === "entering" || status === "preExit";
  const isVisible =
    status === "entering" || status === "entered" || status === "preExit";

  return (
    <div
      data-testid="collapsible-container"
      className={classNames(className, "transition-all", {
        "opacity-100": isVisible,
        "opacity-0": !isVisible,
        "h-auto": isExpanded,
        "h-0": !isExpanded && !hasExplicitHeight,
        "overflow-hidden": !isExpanded,
      })}
      style={
        hasExplicitHeight
          ? { height: innerRef.current?.clientHeight, ...style }
          : style
      }
      onTransitionEnd={(event) => {
        // prevent from firing on bubbled events from inside the container
        if (event.target === event.currentTarget) {
          onTransitionEnd?.(open);
        }
      }}
      {...divProps}
    >
      <div ref={innerRef}>{children}</div>
    </div>
  );
}
